import { useState } from 'react';
import { Row, Col, Form, Button, Alert, Badge, ListGroup, Spinner } from 'react-bootstrap';
import { reposService, ApiError } from '@/services/api';
import type { CfnStack } from '@/services/api/repos-service';

export type SelectedStack = { stackArn: string; stackName: string; region: string };

// Common AWS regions for the picker (operator can still have stacks elsewhere via ARN).
const REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'eu-west-1', 'eu-west-2', 'eu-central-1',
  'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'ca-central-1',
];

/** region from arn:aws:cloudformation:<region>:<acct>:stack/<name>/<id> */
export function regionFromStackArn(arn: string): string {
  const parts = (arn || '').split(':');
  return parts.length > 3 ? parts[3] : '';
}

/** best-effort stack name from a stack ARN's stack/<name>/<id> segment */
export function stackNameFromArn(arn: string): string {
  const m = (arn || '').match(/stack\/([^/]+)\//);
  return m ? m[1] : arn;
}

/**
 * Browse CloudFormation stacks by region (filter by name) and build a selected-stack list.
 * Shared by the onboarding wizard and repo detail so both use the same picker instead of
 * hand-typed ARNs.
 */
export function StackPicker({
  selected,
  setSelected,
}: {
  selected: SelectedStack[];
  setSelected: React.Dispatch<React.SetStateAction<SelectedStack[]>>;
}) {
  const [region, setRegion] = useState('us-west-2');
  const [stacks, setStacks] = useState<CfnStack[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadStacks(r: string) {
    setLoading(true);
    setError(null);
    setStacks([]);
    try {
      const res = await reposService.listStacks(r);
      setStacks(res.items ?? []);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  const needle = filter.trim().toLowerCase();
  const shown = needle ? stacks.filter((s) => s.stackName.toLowerCase().includes(needle)) : stacks;
  const isSelected = (arn: string) => selected.some((s) => s.stackArn === arn);

  function toggle(stack: CfnStack) {
    setSelected((prev) =>
      prev.some((s) => s.stackArn === stack.stackId)
        ? prev.filter((s) => s.stackArn !== stack.stackId)
        : [...prev, { stackArn: stack.stackId, stackName: stack.stackName, region }],
    );
  }

  return (
    <div>
      <Row className="g-2 mb-3">
        <Col md={6}>
          <Form.Select value={region} onChange={(e) => setRegion(e.target.value)}>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Button type="button" variant="outline-primary" onClick={() => loadStacks(region)} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Load stacks'}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {stacks.length > 0 && (
        <>
          <Form.Control
            className="mb-2"
            placeholder="Filter stacks by name — contains"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <ListGroup className="mb-3" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {shown.map((s) => (
            <ListGroup.Item
              key={s.stackId}
              action
              as="button"
              type="button"
              active={isSelected(s.stackId)}
              onClick={(e) => { e.preventDefault(); toggle(s); }}
              className="d-flex justify-content-between align-items-center"
            >
              <span>{s.stackName}</span>
              <Badge bg="light" text="dark">{s.status}</Badge>
            </ListGroup.Item>
          ))}
            {!shown.length && <ListGroup.Item className="text-muted">No stacks match the filter.</ListGroup.Item>}
          </ListGroup>
        </>
      )}

      {selected.length > 0 && (
        <div>
          <strong>Selected stacks ({selected.length}):</strong>
          <ListGroup className="mt-2">
            {selected.map((s) => (
              <ListGroup.Item key={s.stackArn} className="d-flex justify-content-between align-items-center">
                <span>{s.stackName} <Badge bg="secondary">{s.region}</Badge></span>
                <Button type="button" size="sm" variant="outline-danger" onClick={() => setSelected((p) => p.filter((x) => x.stackArn !== s.stackArn))}>
                  Remove
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
}
